import { titleCaseCompany } from '../../utils/formatters'
import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { MessageSquare, SmilePlus, MoreHorizontal, Pencil, Trash2, Link2, Copy, Bookmark, CheckSquare, Megaphone, Lightbulb, Bell, MessagesSquare, CornerUpLeft, Share2, Sparkles, UserPlus, Check, CheckCheck, RotateCcw, FileText, Pin, Languages, Quote, Download, Eye } from 'lucide-react'
import { toast } from '../../stores/uiStore'
import { avatarGradient, initials } from '../crm/pipeline/tokens'
import AttachmentChip from './AttachmentChip'
import VoicePlayer from './VoicePlayer'
import PollCard from './PollCard'
import AttachmentPreview from './AttachmentPreview'
import EmojiPicker from './EmojiPicker'
import { chatService } from '../../services/chatService'
import { renderRichBody } from './richText'

const POST_META = {
  announcement: { label: 'Duyuru', icon: Megaphone, color: 'text-red-600 bg-red-50 border-red-100' },
  discussion: { label: 'Tartışma', icon: MessagesSquare, color: 'text-pink-600 bg-pink-50 border-pink-100' },
  idea: { label: 'Fikir', icon: Lightbulb, color: 'text-amber-600 bg-amber-50 border-amber-100' },
  update: { label: 'Güncelleme', icon: Bell, color: 'text-blue-600 bg-blue-50 border-blue-100' },
}
const QUICK = ['👍', '✅', '🔥']

const fmtTime = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
}
const relTime = (iso) => {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'az önce'
  if (m < 60) return `${m} dk önce`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h} sa önce`
  const g = Math.floor(h / 24)
  if (g < 30) return `${g} gün önce`
  return `${Math.floor(g / 30)} ay önce`
}

export default function MessageRow({ message: m, taskRefs, mentionRefs, myUserId, company, highlight, channelKind,
  onReact, onReply, onEdit, onDelete, onOpenThread, onOpenProfile, onQuoteJump,
  // Desteklenmeyen aksiyonlar gizlenir — dar panelde (ChatDock) kanal asistanı yok.
  // Ölü düğme göstermek, düğmeyi hiç göstermemekten kötüdür.
  hideActions = [],
  // Konu (thread) panelinin içinde mi çiziliyor. ThreadPanel bu prop'u zaten
  // gönderiyordu ama BURADA HİÇ OKUNMUYORDU — yani hiçbir etkisi yoktu.
  inThread = false,
  // Çubuk, satırın GERÇEK genişliğine göre kademelenir (0 = ölçülemedi → en geniş
  // varsayım). Sabit bir dar/geniş bayrağı yetmiyordu: yan panel sürüklenerek
  // daraltılıp genişletilebiliyor, çubuk da onunla birlikte değişmeli.
  // Gizlenen düğmelerin hepsi ⋯ menüsünde durmaya devam eder.
  rowWidth = 0 }) {
  const [menu, setMenu] = useState(null)   // {left, top} — portal (liste overflow'u kırpmasın)
  const [preview, setPreview] = useState(null)  // ek önizleme (yeni sekme yerine popup)
  const [emoji, setEmoji] = useState(null)      // {x,y} — reaksiyon seçici (bar'ın altında açılır)
  const menuBtnRef = useRef(null)
  // Kademeler, çubuğun o düğmelerle kapladığı yaklaşık genişliğe göre seçildi;
  // çubuk satırın yarısından fazlasını kaplarsa metnin/okundu bilgisinin üstüne
  // taşma riski başlıyor.
  const w = rowWidth || 9999
  const showQuick = w >= 620      // 3 hızlı emoji (~110px)
  const showSecondary = w >= 470  // ata · ilet (~70px)
  // Kaydet AYRI kademede: yan panel (ChatDock/konu paneli) 470px'in altında
  // kaldığı için düğme orada hiç çizilmiyordu — kullanıcı "küçük sohbette
  // çalışmıyor" diye bildirdi. Tek düğme ~30px; göreve bağla ile aynı eşik.
  const showSave = w >= 320
  const showTask = w >= 380       // göreve bağla
  const mine = String(m.author_id) === String(myUserId)
  const post = m.post_type ? POST_META[m.post_type] : null

  if (m.is_deleted) {
    return (
      <div data-mid={m.id} className="px-2 py-1 text-[12px] text-gray-300 italic">Bu mesaj silindi</div>
    )
  }

  return (
    <div data-mid={m.id}
      className={`group/m relative flex gap-2.5 px-2 py-1.5 rounded-lg hover:bg-gray-50/70 ${highlight ? 'bg-amber-50 ring-1 ring-amber-200' : ''}`}>
      <button onClick={() => onOpenProfile?.(m.author_id)}
        className={`w-8 h-8 rounded-full bg-gradient-to-br ${avatarGradient(m.author_name)} flex items-center justify-center text-[10px] font-semibold text-white shrink-0 mt-0.5 hover:ring-2 hover:ring-violet-200`}>
        {initials(m.author_name)}
      </button>
      <div className="flex-1 min-w-0">
        {/* Atama / çözüldü barı (ClickUp assign + resolve) */}
        {(m.assigned_to || m.resolved_at) && (
          <div className="flex items-center gap-2 mb-1">
            {m.resolved_at ? (
              <button onClick={() => onEdit?.(m, 'unresolve')}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-[11px] font-medium hover:bg-emerald-100">
                <Check size={11} /> Çözüldü
                <RotateCcw size={10} className="opacity-50" />
              </button>
            ) : (
              <button onClick={() => onEdit?.(m, 'resolve')}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border border-gray-200 text-gray-600 text-[11px] font-medium hover:bg-gray-50">
                <Check size={11} /> Çözüldü işaretle
              </button>
            )}
            {m.assigned_to && (
              <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                Atanan
                <span className={`w-4 h-4 rounded-full bg-gradient-to-br ${avatarGradient(m.assignee_name)} flex items-center justify-center text-[7px] font-semibold text-white`}>
                  {initials(m.assignee_name)}
                </span>
                <span className="font-medium text-gray-700">{m.assignee_name || '—'}</span>
              </span>
            )}
          </div>
        )}
        <div className="flex items-baseline gap-2 pr-2 min-h-[22px]">
          <button onClick={() => onOpenProfile?.(m.author_id)} className="text-[13px] font-semibold text-gray-900 hover:underline">{m.author_name || '—'}</button>
          {m.author_partner && (
            <span title="İş ortağı panelinden yazdı — dış kullanıcı"
              className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap">
              İş ortağı · {titleCaseCompany(m.author_partner)}
            </span>
          )}
          <span className="text-[11px] text-gray-400">{fmtTime(m.created_at)}</span>
          {m.pending ? <span className="text-[10px] text-gray-300">gönderiliyor</span> : null}
          {m.is_edited ? <span className="text-[10px] text-gray-300">(düzenlendi)</span> : null}
          {mine && m.read_total != null && <ReadReceipt m={m} company={company} isDm={channelKind === 'dm'} />}
        </div>

        {post && (
          <div className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-medium ${post.color}`}>
            <post.icon size={11} /> {post.label}
          </div>
        )}
        {m.post_title && <div className="text-[14px] font-semibold text-gray-900 mt-0.5">{m.post_title}</div>}

        {/* "İletildi" rozeti — alıcı mesajın özgün olmadığını görmeli */}
        {m.is_forwarded ? (
          <div className="flex items-center gap-1 mb-0.5 text-[11px] italic text-gray-400">
            <Share2 size={11} />
            {m.forwarded_from ? `İletildi · ${m.forwarded_from}` : 'İletildi'}
          </div>
        ) : null}

        {/* Anket kartı — gövde soruyu taşır, kart oylamayı ekler */}
        {m.poll_id && <PollCard pollId={m.poll_id} company={company} />}

        {/* Sesli mesaj → dosya çipi DEĞİL, oynatıcı */}
        {(m.attachments || []).filter(isAudioAtt).map((a) => (
          <div key={a.id} className="mt-1"><VoicePlayer attachment={a} company={company} /></div>
        ))}

        {(m.attachments || []).filter((a) => isImageAtt(a)).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {m.attachments.filter((a) => isImageAtt(a)).map((a) => (
              <InlineImage key={a.id} att={a} company={company}
                onOpen={() => setPreview((m.attachments || []).findIndex((x) => x.id === a.id))} />
            ))}
          </div>
        )}
        {(m.attachments || []).filter(isVideoAtt).map((a) => (
          <div key={a.id} className="mt-1"><InlineVideo att={a} company={company} /></div>
        ))}
        {(m.attachments || []).filter((a) => isDocAtt(a)).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {m.attachments.filter((a) => isDocAtt(a)).map((a) => (
              <InlineDocCard key={a.id} att={a} company={company}
                onOpen={() => setPreview((m.attachments || []).findIndex((x) => x.id === a.id))} />
            ))}
          </div>
        )}
        {(m.attachments || []).filter((a) => !isImageAtt(a) && !isDocAtt(a) && !isAudioAtt(a) && !isVideoAtt(a)).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {m.attachments.filter((a) => !isImageAtt(a) && !isDocAtt(a) && !isAudioAtt(a) && !isVideoAtt(a)).map((a) => (
              <AttachmentChip key={a.id} att={a} company={company}
                onOpen={() => setPreview((m.attachments || []).findIndex((x) => x.id === a.id))} />
            ))}
          </div>
        )}

        {/* Alıntılanan mesaj (WhatsApp alıntılı yanıt). reply_to_id'den AYRI:
            alıntı mesajı ana akışta BIRAKIR, konu (thread) ise çıkarır. */}
        {m.quote && (
          <button type="button"
            onClick={() => onQuoteJump?.(m.quote.id)}
            className="mb-1.5 w-full text-left border-l-[3px] border-primary-400 bg-gray-50
                       hover:bg-gray-100 rounded-r-md px-2.5 py-1.5 transition-colors">
            <div className="text-[11.5px] font-semibold text-primary-600 truncate">
              {m.quote.author_name}
            </div>
            <div className={`text-[12px] truncate ${m.quote.is_deleted ? 'italic text-gray-400' : 'text-gray-500'}`}>
              {m.quote.is_deleted
                ? 'Bu mesaj silindi'
                : (m.quote.body?.trim() || (m.quote.attachment_count > 0 ? '📎 Ek' : ''))}
            </div>
          </button>
        )}

        {/* Eski yer tutucu '(dosya)' ve boş gövde — ek varken gösterme */}
        {(() => {
          const raw = (m.body || '').trim()
          const placeholder = !raw || raw === '(dosya)'
          if (placeholder && (m.attachments || []).length) return null
          if (m.pending && !raw) return <div className="text-[13px] text-gray-400">Gönderiliyor…</div>
          if (!raw) return null
          return (
            <div className={`text-[13.5px] whitespace-pre-wrap break-words leading-relaxed ${m.resolved_at ? 'text-gray-400' : 'text-gray-800'}`}>
              {renderRichBody(m.body, taskRefs, mentionRefs, onOpenProfile)}
            </div>
          )
        })()}

        {(m.reactions || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {m.reactions.map((r) => {
              const mineR = (r.user_ids || []).map(String).includes(String(myUserId))
              return (
                <button key={r.emoji} onClick={() => onReact?.(m.id, r.emoji)} title={(r.users || []).join(', ')}
                  className={`px-1.5 py-0.5 rounded-full border text-[12px] flex items-center gap-1 ${mineR ? 'bg-violet-50 border-violet-200' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                  {r.emoji} <span className="text-[10px] text-gray-500">{r.count}</span>
                </button>
              )
            })}
          </div>
        )}

        {(m.reply_count || 0) > 0 && (
          <button onClick={() => onOpenThread?.(m.id)} className="flex items-center gap-1.5 mt-1 group/th">
            <span className="flex items-center">
              {(m.thread_participants || []).slice(0, 3).map((p, i) => (
                <span key={p.user_id}
                  className={`w-5 h-5 rounded-full bg-gradient-to-br ${avatarGradient(p.display_name)} ring-2 ring-white flex items-center justify-center text-[8px] font-semibold text-white ${i ? '-ml-1.5' : ''}`}>
                  {initials(p.display_name)}
                </span>
              ))}
            </span>
            <span className="text-[12px] font-medium text-violet-600 group-hover/th:underline">{m.reply_count} yanıt</span>
            <span className="text-[11px] text-gray-400">Son yanıt {relTime(m.last_reply_at)}</span>
          </button>
        )}
      </div>

      {/* Hover aksiyon çubuğu — mesaj satırının TAMAMEN ÜSTÜNDE, sağa dayalı.
          Denenen ve geri alınan iki konum:
          • 'top-8'  → mesaj METNİNİN üstüne biniyordu, kısa mesaj okunmuyordu.
          • '-top-5' → ad/saat/okundu satırıyla kesişiyordu; satırı çubuğun üstüne
            almak (z-katmanı) metni çubuğun İÇİNDEN geçirip "şeffaf çubuk"
            görüntüsü veriyordu, üstelik o şeritte düğmeler tıklanmıyordu.
          '-top-8' hiçbir satır içeriğiyle kesişmez; yukarıda bir önceki mesajın alt
          boşluğuna taşar (Slack/ClickUp'ta da böyle). Düğme sayısı panel genişliğine
          göre kademelenir (rowWidth) — dar panelde sola doğru uzayıp taşmasın. */}
      <div className="absolute -top-8 right-2 hidden group-hover/m:flex items-center gap-0.5 bg-white border border-gray-200 rounded-xl shadow-md px-1.5 py-1 z-[3]">
        {showQuick && QUICK.map((e) => (
          <button key={e} onClick={() => onReact?.(m.id, e)} title={`${e} tepkisi`}
            className="px-1.5 py-0.5 hover:bg-gray-100 rounded-lg text-[15px] leading-none">{e}</button>
        ))}
        <button onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setEmoji({ x: r.left - 120, y: r.bottom + 6 }) }}
          title="Tepki ekle" className="p-1.5 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100"><SmilePlus size={15} /></button>

        {(showQuick || showSecondary) && <span className="w-px h-4 bg-gray-200 mx-1" />}

        {/* "Yanıtla" bu projede KONU açmak demektir (mobil ile aynı).
            Konu panelinin İÇİNDE gizlenir: konu modeli TEK SEVİYE, bir yanıtın
            altına konu açılamaz → düğme orada hiçbir şey yapmıyordu. */}
        {!inThread && (
          <button onClick={() => onOpenThread?.(m.id)} title="Yanıtla (konu aç)" className="p-1.5 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100"><CornerUpLeft size={15} /></button>
        )}
        <button onClick={() => onReply?.(m)} title="Alıntıla" className="p-1.5 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100"><Quote size={15} /></button>
        {showSecondary && <button onClick={(e) => onEdit?.(m, 'assign', e)} title="Birine ata" className="p-1.5 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100"><UserPlus size={15} /></button>}
        {showSecondary && <button onClick={() => onEdit?.(m, 'forward')} title="İlet" className="p-1.5 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100"><Share2 size={15} /></button>}
        {/* Yer imi DURUM taşır: kayıtlıysa dolu/mor çizilir ve tıklama kaydı GERİ ALIR.
            Önceden her tıklama yalnız 'kaydet' yolluyordu → ikinci tıklamada hiçbir
            şey değişmiyor, kullanıcı düğmeyi çalışmıyor sanıyordu. */}
        {showSave && (
          <button onClick={() => onEdit?.(m, m.is_saved ? 'unsave' : 'save')}
            title={m.is_saved ? 'Kayıttan çıkar' : 'Sonra için kaydet'}
            className={`p-1.5 rounded-lg ${m.is_saved
              ? 'text-violet-600 bg-violet-50 hover:bg-violet-100'
              : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`}>
            <Bookmark size={15} fill={m.is_saved ? 'currentColor' : 'none'} />
          </button>
        )}
        {/* Olay NESNESİ geçilir: liste seçici tıklanan düğmenin yanında açılsın.
            Geçilmezse ChatPage sabit "ekran ortası, y=200" konumuna düşüyordu →
            seçici mesajla alakasız bir yerde beliriyordu. */}
        {showTask && <button onClick={(e) => onEdit?.(m, 'task', e)} title="Göreve bağla" className="p-1.5 rounded-lg text-violet-600 hover:bg-violet-50"><CheckSquare size={15} /></button>}
        {showSecondary && !hideActions.includes('ai') && (
          <button onClick={() => onEdit?.(m, 'ai')} title="Kanal asistanı" className="p-1.5 rounded-lg text-pink-500 hover:bg-pink-50"><Sparkles size={15} /></button>
        )}

        {showTask && <span className="w-px h-4 bg-gray-200 mx-1" />}

        <div className="relative">
          <button ref={menuBtnRef} onClick={() => {
              if (menu) return setMenu(null)
              const r = menuBtnRef.current.getBoundingClientRect()
              const H = 320, W = 176
              const top = r.bottom + H > window.innerHeight - 8 ? Math.max(8, r.top - H) : r.bottom + 4
              setMenu({ left: Math.max(8, Math.min(r.right - W, window.innerWidth - W - 8)), top })
            }} className="p-1.5 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100"><MoreHorizontal size={15} /></button>
          {menu && createPortal(
            <>
            <div className="fixed inset-0 z-[90]" onClick={() => setMenu(null)} />
            <div style={{ left: menu.left, top: menu.top }}
              className="fixed bg-white border border-gray-100 rounded-xl shadow-xl py-1 w-44 text-[13px] z-[91]">
              <MenuItem icon={Languages} label="Çevir" onClick={() => { setMenu(null); onEdit?.(m, 'translate') }} />
              <MenuItem icon={Pin} label={m.pinned_at ? 'Sabitlemeyi kaldır' : 'Sabitle'}
                onClick={() => { setMenu(null); onEdit?.(m, m.pinned_at ? 'unpin' : 'pin') }} />
              <MenuItem icon={MessageSquare} label="Okunmadı işaretle" hint="U" onClick={() => { setMenu(null); onEdit?.(m, 'unread') }} />
              <MenuItem icon={Share2} label="İlet" onClick={() => { setMenu(null); onEdit?.(m, 'forward') }} />
              <MenuItem icon={Link2} label="Bağlantıyı kopyala" hint="C" onClick={() => { setMenu(null); onEdit?.(m, 'copylink') }} />
              <MenuItem icon={Copy} label="Metni kopyala" onClick={() => { setMenu(null); onEdit?.(m, 'copytext') }} />
              <div className="my-1 border-t border-gray-100" />
              <MenuItem icon={UserPlus} label={m.assigned_to ? 'Atamayı değiştir' : 'Birine ata'} onClick={(e) => { setMenu(null); onEdit?.(m, 'assign', e) }} />
              <MenuItem icon={Check} label={m.resolved_at ? 'Çözümü geri al' : 'Çözüldü işaretle'} onClick={() => { setMenu(null); onEdit?.(m, m.resolved_at ? 'unresolve' : 'resolve') }} />
              <MenuItem icon={Bookmark} label={m.is_saved ? 'Kayıttan çıkar' : 'Sonra için kaydet'}
                        onClick={() => { setMenu(null); onEdit?.(m, m.is_saved ? 'unsave' : 'save') }} />
              <MenuItem icon={CheckSquare} label="Göreve bağla" onClick={(e) => { setMenu(null); onEdit?.(m, 'task', e) }} />
              <div className="my-1 border-t border-gray-100" />
              {mine && <MenuItem icon={Pencil} label="Düzenle" onClick={() => { setMenu(null); onEdit?.(m, 'edit') }} />}
              <MenuItem icon={Trash2} label="Sil" hint="Del" danger onClick={() => { setMenu(null); onDelete?.(m) }} />
            </div>
            </>, document.body)}
          {preview !== null && (
            <AttachmentPreview items={m.attachments || []} index={preview} company={company} onClose={() => setPreview(null)} />
          )}
          {emoji && (
            <EmojiPicker x={emoji.x} y={emoji.y} onClose={() => setEmoji(null)}
              onPick={(e) => onReact?.(m.id, e)} />
          )}
        </div>
      </div>
    </div>
  )
}

function MenuItem({ icon: Icon, label, onClick, danger, hint }) {
  return (
    <button onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-1.5 text-left ${danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`}>
      <Icon size={13} /> <span className="flex-1">{label}</span>
      {hint && <span className="text-[10px] text-gray-300">{hint}</span>}
    </button>
  )
}

const isImageAtt = (a) => (a.content_type || '').startsWith('image/') || /\.(png|jpe?g|gif|webp)$/i.test(a.file_name || '')
const isPdfAtt = (a) => (a.content_type || '') === 'application/pdf' || /\.pdf$/i.test(a.file_name || '')
const isWordAtt = (a) => /wordprocessingml|msword/i.test(a.content_type || '') || /\.docx$/i.test(a.file_name || '')
const isDocAtt = (a) => isPdfAtt(a) || isWordAtt(a)
// Ses eki: mime güvenilmez (bazı tarayıcılar octet-stream gönderir) → uzantı da bakılır
const isAudioAtt = (a) => (a.content_type || '').startsWith('audio/') || /\.(m4a|aac|mp3|ogg|wav|webm)$/i.test(a.file_name || '')
// Video eki — mobilden (galeri/kamera) gelen mp4/mov; .webm ses için ayrılmıştır (isAudioAtt önce bakılır)
const isVideoAtt = (a) => !isAudioAtt(a) && ((a.content_type || '').startsWith('video/') || /\.(mp4|mov|m4v|3gp)$/i.test(a.file_name || ''))

/** Video → akışta gömülü oynatıcı. Bayt Bearer ister → blob ilk tıklamada çekilir
 *  (her mesaj için otomatik indirme akışı yavaşlatır). */
function InlineVideo({ att, company }) {
  const [url, setUrl] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  useEffect(() => () => { if (url) URL.revokeObjectURL(url) }, [url])
  const load = async () => {
    setBusy(true); setErr(null)
    try {
      let blob = await chatService.fetchAttachmentBlob(company, att.id)
      if (!(blob.type || '').startsWith('video/')) {
        const ext = ((att.file_name || '').split('.').pop() || '').toLowerCase()
        const t = { mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime', '3gp': 'video/3gpp' }[ext]
        if (t) blob = new Blob([blob], { type: t })
      }
      setUrl(URL.createObjectURL(blob))
    } catch { setErr('Video açılamadı') }
    finally { setBusy(false) }
  }
  if (url) {
    return <video src={url} controls autoPlay playsInline className="max-h-64 max-w-[320px] rounded-lg bg-black" />
  }
  return (
    <button type="button" onClick={load} disabled={busy} title={att.file_name}
      className="relative w-56 h-32 rounded-lg bg-slate-800 text-white flex items-center justify-center hover:bg-slate-700">
      <span className="w-11 h-11 rounded-full bg-white/70 flex items-center justify-center text-slate-900">
        {busy ? <span className="w-4 h-4 border-2 border-slate-900/40 border-t-slate-900 rounded-full animate-spin" /> : '▶'}
      </span>
      <span className="absolute left-2 right-2 bottom-1.5 text-[10.5px] text-white/70 truncate text-left">
        {err || att.file_name}
      </span>
    </button>
  )
}

// Auth gerektiren resim — blob → objectURL (unmount'ta revoke).
// İstek başarısız olursa TEKRAR DENER: ek, mesaj kaydedildikten hemen sonra
// yüklendiği için dinleyen istemci arada çekerse 404 alabiliyor. Eski kod hatayı
// yutuyordu ve kutu sonsuza dek gri iskelet kalıyordu — sessiz başarısızlık.
function InlineImage({ att, company, onOpen }) {
  const [url, setUrl] = useState(null)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    let alive = true, made = null, timer = null
    let tries = 0
    const load = () => {
      chatService.fetchAttachmentBlob(company, att.id).then((b) => {
        if (!alive) return
        made = URL.createObjectURL(b)
        setUrl(made); setFailed(false)
      }).catch(() => {
        if (!alive) return
        tries += 1
        if (tries <= 3) timer = setTimeout(load, tries * 800)
        else setFailed(true)
      })
    }
    setUrl(null); setFailed(false); load()
    return () => { alive = false; if (timer) clearTimeout(timer); if (made) URL.revokeObjectURL(made) }
  }, [company, att.id, attempt])
  if (failed) {
    return (
      <button type="button" onClick={() => setAttempt((n) => n + 1)}
        className="w-40 h-28 rounded-lg border border-dashed border-gray-300 bg-gray-50 text-[11px] text-gray-500 hover:bg-gray-100 flex flex-col items-center justify-center gap-1">
        <span>Görsel yüklenemedi</span><span className="underline">Tekrar dene</span>
      </button>
    )
  }
  if (!url) return <div className="w-40 h-28 rounded-lg bg-gray-100 animate-pulse" />
  return (
    <button type="button" onClick={onOpen} title={att.file_name} className="block">
      <img src={url} alt={att.file_name} className="max-h-56 max-w-[320px] rounded-lg border border-gray-200 object-contain bg-white hover:opacity-90" />
    </button>
  )
}

/** PDF / Word — akışta net dosya kartı. Tıkla → tam önizleme, İndir → doğrudan indir.
 *  ⚠️ Eskiden PDF'i <iframe> ile gömüyordu; tarayıcının "PDF'leri indir" ayarı veya
 *  iframe-içi PDF davranışı yüzünden koyu bir "Open" placeholder'ı çıkıp "açılmıyor"
 *  sanılıyordu. Artık gömme YOK — önizleme modalda, indirme blob ile (tarayıcı ayarından
 *  bağımsız her yerde çalışır). */
function InlineDocCard({ att, company, onOpen }) {
  const pdf = isPdfAtt(att)
  const [busy, setBusy] = useState(false)
  const download = async (e) => {
    e.stopPropagation()
    if (busy) return
    setBusy(true)
    try {
      const blob = await chatService.fetchAttachmentBlob(company, att.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = att.file_name || 'dosya'
      document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (err) {
      toast.error('İndirilemedi', err.response?.data?.detail || err.message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="w-[240px] rounded-xl border border-gray-200 bg-white overflow-hidden hover:border-violet-300 hover:shadow-sm">
      <button type="button" onClick={onOpen} title={`${att.file_name} — önizle`}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left">
        <span className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${pdf ? 'bg-red-50' : 'bg-blue-50'}`}>
          <FileText size={18} className={pdf ? 'text-red-500' : 'text-blue-600'} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[12.5px] font-medium text-gray-800 truncate">{att.file_name}</span>
          <span className="block text-[10.5px] text-gray-400">{pdf ? 'PDF' : 'Word'}{att.size_bytes ? ` · ${fmtDocSize(att.size_bytes)}` : ''}</span>
        </span>
      </button>
      <div className="flex border-t border-gray-100 divide-x divide-gray-100">
        <button type="button" onClick={onOpen}
          className="flex-1 flex items-center justify-center gap-1 py-1.5 text-[11px] font-medium text-gray-600 hover:bg-gray-50">
          <Eye size={13} /> Önizle
        </button>
        <button type="button" onClick={download} disabled={busy}
          className="flex-1 flex items-center justify-center gap-1 py-1.5 text-[11px] font-medium text-violet-600 hover:bg-violet-50 disabled:opacity-50">
          <Download size={13} /> {busy ? 'İniyor…' : 'İndir'}
        </button>
      </div>
    </div>
  )
}

const fmtDocSize = (b) => (!b ? '' : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`)

/**
 * Okundu bilgisi — yalnız kendi mesajlarında.
 * DM: ✓ gönderildi / ✓✓ okundu (WhatsApp).  Kanal: "N okudu" → tıkla, kim okudu listesi.
 * "İletildi" (gri çift tik) yok — presence/delivery takibi tutulmuyor.
 */
function ReadReceipt({ m, company, isDm }) {
  const [pop, setPop] = useState(null)      // {x, y}
  const [data, setData] = useState(null)
  const read = m.read_count || 0
  const total = m.read_total || 0
  const allRead = total > 0 && read >= total

  // Liste ALTINDAKİ mesajlarda pencere aşağı açılınca ekrandan taşıyordu — okuyanlar
  // listesi kırpılıyordu (2026-08-27). Aşağıda yer yoksa YUKARI açılır; her iki yönde
  // de yükseklik kalan boşluğa göre sınırlanır, böylece hiçbir konumda taşma olmaz.
  // Yukarı açarken 'top' değil 'bottom' kullanılır: içeriğin yüksekliği (kaç kişi
  // okudu) daha bilinmiyor, alt kenarı düğmeye sabitlemek ölçüm gerektirmez.
  const open = (e) => {
    if (!total) return
    const r = e.currentTarget.getBoundingClientRect()
    const below = window.innerHeight - r.bottom - 12
    const above = r.top - 12
    const flip = below < 180 && above > below
    setPop({
      x: Math.max(8, Math.min(r.left, window.innerWidth - 240)),
      top: flip ? undefined : r.bottom + 4,
      bottom: flip ? window.innerHeight - r.top + 4 : undefined,
      maxH: Math.max(140, Math.min(288, flip ? above : below)),
    })
    if (!data) chatService.messageReaders(company, m.id).then((res) => setData(res?.data || null)).catch(() => {})
  }

  if (isDm) {
    return (
      <span title={allRead ? 'Okundu' : 'Gönderildi'} className={allRead ? 'text-blue-500' : 'text-gray-300'}>
        {allRead ? <CheckCheck size={13} /> : <Check size={13} />}
      </span>
    )
  }
  return (
    <>
      <button onClick={open} disabled={!total}
        className={`inline-flex items-center gap-0.5 text-[10px] ${read ? 'text-blue-500 hover:underline' : 'text-gray-300'}`}>
        {read ? <CheckCheck size={12} /> : <Check size={12} />}
        {total ? <span>{read}/{total}</span> : null}
      </button>
      {pop && createPortal(
        <div className="fixed inset-0 z-[95]" onMouseDown={() => setPop(null)}>
          <div className="fixed w-56 overflow-y-auto bg-white rounded-xl border border-gray-200 shadow-2xl py-1.5"
            style={{ left: pop.x, top: pop.top, bottom: pop.bottom, maxHeight: pop.maxH }}
            onMouseDown={(e) => e.stopPropagation()}>
            {!data ? (
              <div className="px-3 py-2 text-[12px] text-gray-400">Yükleniyor…</div>
            ) : (
              <>
                <div className="px-3 pt-1 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  Okuyanlar ({data.read?.length || 0})
                </div>
                {(data.read || []).map((u) => <ReaderRow key={u.user_id} u={u} read />)}
                {!(data.read || []).length && <div className="px-3 py-1 text-[12px] text-gray-400">Henüz kimse okumadı</div>}
                {(data.unread || []).length > 0 && (
                  <>
                    <div className="px-3 pt-2 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                      Okumayanlar ({data.unread.length})
                    </div>
                    {data.unread.map((u) => <ReaderRow key={u.user_id} u={u} />)}
                  </>
                )}
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

function ReaderRow({ u, read }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1">
      <span className={`w-5 h-5 rounded-full bg-gradient-to-br ${avatarGradient(u.display_name)} flex items-center justify-center text-[8px] font-semibold text-white shrink-0`}>
        {initials(u.display_name)}
      </span>
      <span className={`text-[12px] truncate flex-1 ${read ? 'text-gray-700' : 'text-gray-400'}`}>{u.display_name}</span>
      {read ? <CheckCheck size={12} className="text-blue-500 shrink-0" /> : <Check size={12} className="text-gray-300 shrink-0" />}
    </div>
  )
}
